import { useTranslation } from '@wix/yoshi-flow-editor';
import type { FC } from 'react';
import React from 'react';
import {
  Button,
  ButtonPriority,
  HoverStyles,
  ThreeDotsLoader,
} from '@wix/wix-ui-tpa/cssVars';

import { useBlogPostsHandlers } from '../../../../../contexts/widgets';
import type { BlogPost } from '../../../../../types';
import { DataHook } from '../../../../../types';
import { usePostListFocus } from './hooks';
import Post from './Post';
import { classes } from './PostList.st.css';

interface PostListProps {
  posts: BlogPost[];
  paging: { count: number; offset: number; total: number };
  arePostsLoading: boolean;
}

export const PostList: FC<PostListProps> = ({
  posts,
  paging,
  arePostsLoading,
}) => {
  const { t } = useTranslation();
  const { fetchPosts } = useBlogPostsHandlers();
  const { indexToFocus, focusRef } = usePostListFocus(
    arePostsLoading,
    paging.count,
  );

  const shouldShowLoadMoreButton = paging.total > paging.count;

  return (
    <div className={classes.root}>
      <div className={classes.posts}>
        {posts.map((post, index) => {
          return (
            <Post
              key={post.id}
              ref={(ref) =>
                index === indexToFocus.current && (focusRef.current = ref)
              }
              {...post}
            />
          );
        })}
      </div>
      {shouldShowLoadMoreButton && (
        <div className={classes.loadMoreButtonContainer}>
          {arePostsLoading ? (
            <ThreeDotsLoader
              data-hook={DataHook.PostsLoader}
              className={classes.loader}
            />
          ) : (
            <Button
              type="button"
              data-hook={DataHook.LoadMorePostsButton}
              className={classes.loadMoreButton}
              priority={ButtonPriority.secondary}
              upgrade={true}
              onClick={() => {
                indexToFocus.current = paging.count;
                fetchPosts();
              }}
              hoverStyle={HoverStyles.underline}
            >
              {t('recent-posts.load-more')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
